/*
 * <pdp-demo-video> — custom, accessible player for the product-details "See it in action" video.
 * Wraps a native <video> so play/pause, mute/unmute, seek and fullscreen are fully controllable.
 */
if (!customElements.get('pdp-demo-video')) {
  customElements.define(
    'pdp-demo-video',
    class PdpDemoVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video) return;

        this.overlayPlay = this.querySelector('[data-overlay-play]');
        this.playToggle = this.querySelector('[data-play-toggle]');
        this.muteToggle = this.querySelector('[data-mute-toggle]');
        this.fullscreenToggle = this.querySelector('[data-fullscreen-toggle]');
        this.scrubber = this.querySelector('[data-scrubber]');
        this.progressFill = this.querySelector('[data-progress-fill]');
        this.currentTimeEl = this.querySelector('[data-current-time]');
        this.durationEl = this.querySelector('[data-duration]');
        this.media = this.querySelector('[data-media]');

        this.idleTimer = null;
        this.autoplay = this.hasAttribute('data-autoplay');

        this.bindEvents();
        this.syncMuteUI();
        this.syncFullscreenUI();

        // If configured to autoplay, it must be muted to satisfy browser policies.
        if (this.autoplay) {
          this.video.muted = true;
          this.observeForAutoplay();
        }
      }

      bindEvents() {
        // Click the video surface to toggle playback. Bound to the <video> element
        // itself (not the media wrapper) so clicks on the overlay button and the
        // control bar don't bubble back and double-toggle play/pause.
        this.video.addEventListener('click', () => this.togglePlay());
        if (this.overlayPlay) this.overlayPlay.addEventListener('click', () => this.togglePlay());
        if (this.playToggle) this.playToggle.addEventListener('click', () => this.togglePlay());
        if (this.muteToggle) this.muteToggle.addEventListener('click', () => this.toggleMute());
        if (this.fullscreenToggle)
          this.fullscreenToggle.addEventListener('click', () => this.toggleFullscreen());

        this.video.addEventListener('play', () => {
          this.classList.add('is-playing');
          this.setPressed(this.playToggle, true);
          this.resetIdleTimer();
        });
        this.video.addEventListener('pause', () => {
          this.classList.remove('is-playing');
          this.setPressed(this.playToggle, false);
          this.showControls();
        });
        this.video.addEventListener('ended', () => {
          this.classList.remove('is-playing');
          this.showControls();
        });
        this.video.addEventListener('volumechange', () => this.syncMuteUI());
        this.video.addEventListener('loadedmetadata', () => {
          if (this.durationEl) this.durationEl.textContent = this.formatTime(this.video.duration);
          if (this.scrubber) this.scrubber.max = '1000';
        });
        this.video.addEventListener('timeupdate', () => this.updateProgress());

        // Scrubbing.
        if (this.scrubber) {
          this.scrubber.addEventListener('input', () => {
            if (!this.video.duration) return;
            const ratio = this.scrubber.value / (this.scrubber.max || 1000);
            this.video.currentTime = ratio * this.video.duration;
            this.updateProgressFill(ratio);
          });
        }

        // Fullscreen state changes (standard + WebKit).
        ['fullscreenchange', 'webkitfullscreenchange'].forEach((evt) =>
          document.addEventListener(evt, () => this.syncFullscreenUI())
        );

        // Auto-hide controls while playing; reveal on interaction.
        this.addEventListener('pointermove', () => this.resetIdleTimer());
        this.addEventListener('pointerleave', () => {
          if (!this.video.paused && !this.matches(':focus-within')) this.hideControls();
        });
        this.addEventListener('focusin', () => this.showControls());

        // Keyboard shortcuts when the player has focus.
        this.setAttribute('tabindex', this.getAttribute('tabindex') || '0');
        this.addEventListener('keydown', (event) => this.onKeydown(event));
      }

      togglePlay() {
        if (this.video.paused) {
          const played = this.video.play();
          if (played && typeof played.catch === 'function') played.catch(() => {});
        } else {
          this.video.pause();
        }
      }

      toggleMute() {
        this.video.muted = !this.video.muted;
      }

      toggleFullscreen() {
        const fsElement = document.fullscreenElement || document.webkitFullscreenElement;
        if (fsElement) {
          (document.exitFullscreen || document.webkitExitFullscreen).call(document);
          return;
        }
        if (this.requestFullscreen) {
          this.requestFullscreen();
        } else if (this.webkitRequestFullscreen) {
          this.webkitRequestFullscreen();
        } else if (this.video.webkitEnterFullscreen) {
          // iOS Safari only supports fullscreen on the <video> element itself.
          this.video.webkitEnterFullscreen();
        }
      }

      onKeydown(event) {
        switch (event.key) {
          case ' ':
          case 'k':
            event.preventDefault();
            this.togglePlay();
            break;
          case 'm':
            this.toggleMute();
            break;
          case 'f':
            this.toggleFullscreen();
            break;
          case 'ArrowRight':
            if (this.video.duration) this.video.currentTime = Math.min(this.video.duration, this.video.currentTime + 5);
            break;
          case 'ArrowLeft':
            this.video.currentTime = Math.max(0, this.video.currentTime - 5);
            break;
          default:
            return;
        }
      }

      updateProgress() {
        if (!this.video.duration) return;
        const ratio = this.video.currentTime / this.video.duration;
        if (this.scrubber && document.activeElement !== this.scrubber) {
          this.scrubber.value = Math.round(ratio * (this.scrubber.max || 1000));
        }
        this.updateProgressFill(ratio);
        if (this.currentTimeEl) this.currentTimeEl.textContent = this.formatTime(this.video.currentTime);
      }

      updateProgressFill(ratio) {
        if (this.progressFill) this.progressFill.style.transform = `scaleX(${ratio})`;
        if (this.scrubber) this.scrubber.setAttribute('aria-valuetext', this.formatTime(this.video.currentTime));
      }

      syncMuteUI() {
        const muted = this.video.muted || this.video.volume === 0;
        this.classList.toggle('is-muted', muted);
        this.setPressed(this.muteToggle, muted);
        if (this.muteToggle) {
          this.muteToggle.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
        }
      }

      syncFullscreenUI() {
        const isFs = !!(document.fullscreenElement === this || document.webkitFullscreenElement === this);
        this.classList.toggle('is-fullscreen', isFs);
        this.setPressed(this.fullscreenToggle, isFs);
        if (this.fullscreenToggle) {
          this.fullscreenToggle.setAttribute('aria-label', isFs ? 'Exit fullscreen' : 'Open fullscreen');
        }
      }

      setPressed(button, state) {
        if (button) button.setAttribute('aria-pressed', state ? 'true' : 'false');
      }

      showControls() {
        this.classList.remove('is-idle');
        clearTimeout(this.idleTimer);
      }

      hideControls() {
        this.classList.add('is-idle');
      }

      resetIdleTimer() {
        this.showControls();
        if (this.video.paused) return;
        this.idleTimer = setTimeout(() => {
          if (!this.video.paused && !this.matches(':focus-within')) this.hideControls();
        }, 2600);
      }

      observeForAutoplay() {
        if (!('IntersectionObserver' in window)) return;
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const played = this.video.play();
                if (played && typeof played.catch === 'function') played.catch(() => {});
              } else {
                this.video.pause();
              }
            });
          },
          { threshold: 0.5 }
        );
        observer.observe(this);
      }

      formatTime(seconds) {
        if (!seconds || Number.isNaN(seconds)) return '0:00';
        const total = Math.floor(seconds);
        const mins = Math.floor(total / 60);
        const secs = total % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
      }
    }
  );
}
